import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Plus, X, Sparkles, RefreshCw, Copy, Check, Heart, Zap, FlowerIcon, FileText } from 'lucide-react';
import LiteShell, { StepIndicator } from '../LiteShell';
import RateLimitGate from '@/components/lite/RateLimitGate';
import ActivityShareImage from '@/components/lite/ActivityShareImage';
import LiteFeedback from '@/components/lite/LiteFeedback';
import { liteApi } from '@/utils/lite/liteApi';

const COMMUNICATION_STYLES = [
  { value: 'GLP', label: 'GLP — Gestalt Language Processor' },
  { value: 'Analytic', label: 'Analytic — single words → grammar' },
  { value: 'AAC', label: 'AAC — multimodal communicator' },
  { value: 'Mixed', label: 'Mixed — multiple modalities' },
  { value: 'Emerging', label: 'Emerging communicator' },
];

const REGULATION = [
  { value: 'calm', label: 'Calm / quiet' },
  { value: 'active', label: 'Active / movement' },
  { value: 'sensory', label: 'Sensory-seeking' },
  { value: 'flexible', label: 'Flexible (any)' },
];

const GLP_STAGES = [
  { value: 1, label: 'Stage 1 — Echolalia / whole gestalts' },
  { value: 2, label: 'Stage 2 — Mitigated gestalts' },
  { value: 3, label: 'Stage 3 — Single words extracted' },
  { value: 4, label: 'Stage 4 — Novel 2-3 word combos' },
  { value: 5, label: 'Stage 5 — Emerging grammar' },
  { value: 6, label: 'Stage 6 — Complex sentences' },
];

const GOAL_AREAS = [
  'Receptive language', 'Expressive language', 'Pragmatics / social',
  'Articulation / phonology', 'Fluency', 'AAC use', 'Joint attention',
  'Regulation', 'Play skills', 'Vocabulary',
];

const POPULAR_INTERESTS = [
  'trains', 'cars', 'animals', 'dinosaurs', 'water play', 'music',
  'numbers', 'letters', 'sensory bins', 'bubbles', 'building blocks',
  'pretend play', 'dolls', 'cooking', 'art / drawing',
];

export default function ActivityGenerator() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    child_age: '',
    glp_stage: '',
    interests: [],
    interestInput: '',
    goal_area: '',
    custom_goal: '',
    materials: '',
    communication_style: 'GLP',
    regulation_preference: 'flexible',
  });
  const [activity, setActivity] = useState(null);
  const [usage, setUsage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState({ used: 0, limit: 0 });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addInterest = (val) => {
    const v = (val || form.interestInput).trim();
    if (!v) return;
    if (form.interests.includes(v)) return;
    update('interests', [...form.interests, v]);
    update('interestInput', '');
  };
  const removeInterest = (i) => update('interests', form.interests.filter((x) => x !== i));

  const generate = async () => {
    setError('');
    setGenerating(true);
    setActivity(null);
    try {
      let email = null;
      try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
      const payload = {
        child_age: form.child_age ? parseInt(form.child_age, 10) : null,
        glp_stage: form.glp_stage ? parseInt(form.glp_stage, 10) : null,
        child_interests: form.interests,
        goals: [],
        custom_goal: form.custom_goal,
        goal_area: form.goal_area,
        materials: form.materials,
        communication_style: form.communication_style,
        regulation_preference: form.regulation_preference,
        confirm_deidentified: true,
        email,
      };
      const res = await liteApi.generateActivity(payload);
      setActivity(res.activity);
      setUsage({ used_today: res.used_today, daily_limit: res.daily_limit });
      setStep(2);
    } catch (e) {
      if (e.status === 429) {
        const d = e.data?.detail || {};
        setGateInfo({ used: d.used || 0, limit: d.limit || 5 });
        setGateOpen(true);
      } else {
        setError(e.message || 'Generation failed.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!activity) return;
    const text = formatActivityAsText(activity);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {/* ignore */}
  };

  return (
    <LiteShell currentStep={step} totalSteps={2}>
      <h1 className="text-3xl font-serif text-primary-900 mb-2">Activity Generator</h1>
      <p className="text-stone-500 mb-6">
        Neurodiversity-affirming play activities tailored to GLP stage, interests, and goals.
      </p>
      <StepIndicator step={step} labels={['Inputs', 'Activity']} />

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lite-activity-error">{error}</div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          {/* Child profile */}
          <section className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Child's age (years)</Label>
                <Input
                  type="number" min="1" max="21"
                  placeholder="e.g. 6"
                  value={form.child_age}
                  onChange={(e) => update('child_age', e.target.value)}
                  data-testid="activity-age-input"
                />
                <p className="text-xs text-stone-500">Used for age-appropriate materials only — not for setting language expectations.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Communication style</Label>
                <Select value={form.communication_style} onValueChange={(v) => update('communication_style', v)}>
                  <SelectTrigger data-testid="activity-style-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMUNICATION_STYLES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>GLP stage (optional)</Label>
                <Select value={form.glp_stage} onValueChange={(v) => update('glp_stage', v)}>
                  <SelectTrigger data-testid="activity-glp-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent>{GLP_STAGES.map(s => <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-stone-500">Activity language examples will match this stage.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Regulation preference</Label>
                <Select value={form.regulation_preference} onValueChange={(v) => update('regulation_preference', v)}>
                  <SelectTrigger data-testid="activity-reg-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGULATION.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Interests */}
          <section className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
            <div>
              <Label>Child's interests</Label>
              <p className="text-xs text-stone-500 mt-1">Even unconventional ones (pool pumps, washing machines, fans) — the AI honors them.</p>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Type an interest and press Enter"
                value={form.interestInput}
                onChange={(e) => update('interestInput', e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInterest(); } }}
                data-testid="activity-interest-input"
              />
              <Button type="button" variant="outline" onClick={() => addInterest()} className="rounded-full" data-testid="activity-add-interest-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.interests.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.interests.map(i => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm" data-testid={`activity-interest-chip-${i}`}>
                    {i}
                    <button type="button" onClick={() => removeInterest(i)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="pt-2 border-t border-stone-100">
              <p className="text-xs uppercase tracking-wide text-stone-400 mb-2">Or pick from popular interests</p>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_INTERESTS.filter(p => !form.interests.includes(p)).map(p => (
                  <button
                    key={p} type="button"
                    onClick={() => addInterest(p)}
                    className="text-xs px-2.5 py-1 rounded-full border border-stone-200 hover:bg-stone-50 text-stone-600"
                    data-testid={`activity-popular-${p.replace(/\s+/g, '-')}`}
                  >+ {p}</button>
                ))}
              </div>
            </div>
          </section>

          {/* Goals + materials */}
          <section className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Goal area (optional)</Label>
                <Select value={form.goal_area} onValueChange={(v) => update('goal_area', v)}>
                  <SelectTrigger data-testid="activity-goal-area-select"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>{GOAL_AREAS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Specific goal (optional)</Label>
                <Input
                  placeholder="e.g. Use 2-word combinations during play"
                  value={form.custom_goal}
                  onChange={(e) => update('custom_goal', e.target.value)}
                  data-testid="activity-custom-goal-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Available materials (optional)</Label>
              <Textarea
                placeholder="e.g. small toy trains, water table, plastic cups"
                value={form.materials}
                onChange={(e) => update('materials', e.target.value)}
                rows={2}
                data-testid="activity-materials-input"
              />
            </div>
          </section>

          <div className="flex justify-end pt-2">
            <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="activity-generate-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate activity</>}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && activity && (
        <div className="space-y-4" data-testid="activity-result">
          <div className="rounded-2xl border border-stone-200 bg-white p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-stone-100">
              <div>
                <h2 className="text-2xl font-serif text-primary-900">{activity.name}</h2>
                <p className="text-sm text-stone-500 capitalize">{activity.play_type}</p>
              </div>
              {usage && (
                <span className="text-xs text-stone-500">
                  {Math.max(0, usage.daily_limit - usage.used_today)} of {usage.daily_limit} left today
                </span>
              )}
            </div>

            <Pills label="Communication functions" items={activity.communication_functions || []} icon={<Heart className="w-3 h-3" />} />
            <Pill label="Regulation level" value={activity.regulation_level} icon={<Zap className="w-3 h-3" />} />

            {activity.materials_list?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-2">What you'll need</h3>
                <div className="flex flex-wrap gap-1.5">
                  {activity.materials_list.map((m, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-stone-100 text-stone-800 text-sm" data-testid={`activity-mat-${i}`}>
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {activity.steps?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-2">What to do</h3>
                <ol className="space-y-2">
                  {activity.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm text-stone-800" data-testid={`activity-step-${i}`}>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white inline-flex items-center justify-center text-xs font-semibold">
                        {i + 1}
                      </span>
                      <span className="flex-1 leading-relaxed pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {activity.language_examples?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-1.5">
                  <FlowerIcon className="w-3.5 h-3.5 text-primary" />
                  Language to model
                  {form.glp_stage && <span className="text-xs font-normal text-stone-500">(Stage {form.glp_stage} appropriate)</span>}
                </h3>
                <div className="grid gap-1.5">
                  {activity.language_examples.map((ex, i) => (
                    <div key={i} className="px-3 py-2 rounded-lg bg-primary/5 border border-primary/10 text-sm text-stone-800" data-testid={`activity-lang-${i}`}>
                      "{ex}"
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activity.follow_lead_guidance && (
              <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-1.5">Follow the child's lead</h3>
                <p className="text-sm text-stone-700 leading-relaxed">{activity.follow_lead_guidance}</p>
              </div>
            )}

            {activity.notes && (
              <div className="pt-3 border-t border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900 mb-1.5">Therapy notes</h3>
                <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{activity.notes}</p>
              </div>
            )}

            <p className="text-xs text-stone-400 pt-3 border-t border-stone-100 italic">
              AI-generated draft. Adapt to your child and clinical judgment.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleCopy} variant="outline" className="rounded-full" data-testid="activity-copy-btn">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button onClick={() => setShareOpen(true)} variant="outline" className="rounded-full" data-testid="activity-share-ig-btn">
              <FileText className="w-4 h-4 mr-2" /> Parent handout
            </Button>
            <Button onClick={generate} variant="outline" className="rounded-full" data-testid="activity-regenerate-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
            </Button>
            <Button onClick={() => { setActivity(null); setStep(1); }} variant="ghost" className="rounded-full" data-testid="activity-startover-btn">
              New activity
            </Button>
          </div>

          {/* Anonymous thumbs up/down feedback */}
          <LiteFeedback tool="activity" outputKey={activity?.name || ''} />
        </div>
      )}

      <RateLimitGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        used={gateInfo.used}
        limit={gateInfo.limit}
        onUnlocked={() => setGateOpen(false)}
      />

      {shareOpen && activity && (
        <ActivityShareImage
          activity={activity}
          materialsFallback={form.materials}
          onClose={() => setShareOpen(false)}
        />
      )}
    </LiteShell>
  );
}

function Pill({ label, value, icon }) {
  if (!value) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-stone-700 text-sm">
      {icon}<span className="font-medium">{label}:</span> <span className="capitalize">{value}</span>
    </div>
  );
}

function Pills({ label, items, icon }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-stone-700 inline-flex items-center gap-1.5">{icon}{label}:</span>
      {items.map((it, i) => (
        <span key={i} className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-xs">{it}</span>
      ))}
    </div>
  );
}

function formatActivityAsText(a) {
  const lines = [
    a.name || 'Activity',
    a.play_type ? `Type: ${a.play_type}` : '',
    a.regulation_level ? `Regulation level: ${a.regulation_level}` : '',
    a.communication_functions?.length ? `Communication functions: ${a.communication_functions.join(', ')}` : '',
    '',
  ];
  if (a.materials_list?.length) {
    lines.push('What you\'ll need:');
    a.materials_list.forEach((m) => lines.push(`  • ${m}`));
    lines.push('');
  }
  if (a.steps?.length) {
    lines.push('What to do:');
    a.steps.forEach((s, i) => lines.push(`  ${i + 1}. ${s}`));
    lines.push('');
  }
  if (a.language_examples?.length) {
    lines.push('Language to model:');
    a.language_examples.forEach((ex) => lines.push(`  • "${ex}"`));
    lines.push('');
  }
  if (a.follow_lead_guidance) {
    lines.push('Follow the child\'s lead:', a.follow_lead_guidance, '');
  }
  if (a.notes) {
    lines.push('Therapy notes:', a.notes, '');
  }
  lines.push('— AI-generated draft. Adapt to your child and clinical judgment.');
  return lines.filter((l) => l !== null && l !== undefined).join('\n');
}
